/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { forwardRef, Fragment, useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Link } from 'react-resource-router';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import ActivityIcon from '@atlaskit/icon/core/dashboard';
import PageIcon from '@atlaskit/icon/core/page';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';
import { EmptyState } from '@atlassian/search-dialog/empty-state';
import { SearchDialogContent } from '@atlassian/search-dialog/search-dialog.styled';
import type { LinkComponentProps } from '@atlassian/search-dialog/search-link-component';
import { SearchResult } from '@atlassian/search-dialog/search-result';

import { SearchErrorImage } from '../../../article-search-component/search-dropdown/resolved-and-error-content/error-image';
import type { helpObjectSearchResultsQuery } from './__generated__/helpObjectSearchResultsQuery.graphql';
import { messages } from './messages';
import { SplitHelpObjectSearchResults } from './split-help-object-search-results';

type ArticleResult = {
	ari: string;
	isExternal: boolean;
	title: string;
	type: 'ARTICLE';
	url: string;
};

type RequestTypeResult = {
	iconUrl: string | null | undefined;
	id: string;
	title: string;
	type: 'REQUEST_TYPE';
	url: string;
};

type HelpObjectSearchResultsProps = {
	cloudId: string;
	helpCenterAri: string;
	queryWord: string;
};

const DEFAULT_RESULTS_LIMIT = 3;

export const HelpObjectSearchError = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<EmptyState
			title={formatMessage(messages.errorTitle)}
			Image={SearchErrorImage}
			content={<FormattedMessage {...messages.errorDescription} />}
		/>
	);
};

const ExternalLinkComponent = forwardRef<HTMLAnchorElement, LinkComponentProps>(
	({ href = '#', ...props }, ref) => (
		<Anchor
			ref={ref}
			href={href}
			{...props}
			target="_blank"
			xcss={styles.externalLink}
			rel="noopener noreferrer"
		/>
	),
);

const InternalLinkComponent = forwardRef<HTMLAnchorElement, LinkComponentProps>(
	({ href = '#', children, ...props }, ref) => (
		<Link ref={ref} to={href} {...props}>
			{children}
		</Link>
	),
);

const CombinedHelpObjectSearchResults = ({
	cloudId,
	helpCenterAri,
	queryWord,
}: HelpObjectSearchResultsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	/* eslint-disable @atlassian/relay/unused-fields */
	const searchResponse = useLazyLoadQuery<helpObjectSearchResultsQuery>(
		graphql`
			query helpObjectSearchResultsQuery(
				$cloudId: ID!
				$helpCenterAri: ID
				$queryTerm: String!
				$limit: Int!
			) {
				helpObjectStore_searchArticles(
					cloudId: $cloudId
					queryTerm: $queryTerm
					limit: $limit
					helpCenterAri: $helpCenterAri
					highlight: false
				) {
					... on HelpObjectStoreArticleSearchResults {
						results {
							ari
							title
							displayLink
							metadata {
								isExternal
							}
						}
					}
					... on HelpObjectStoreSearchError {
						message
					}
				}
				helpObjectStore_searchRequestTypes(
					cloudId: $cloudId
					queryTerm: $queryTerm
					limit: $limit
					helpCenterAri: $helpCenterAri
				) {
					... on HelpObjectStoreRequestTypeSearchResults {
						results {
							id
							title
							displayLink
							iconUrl
						}
					}
					... on HelpObjectStoreSearchError {
						message
					}
				}
			}
		`,
		{
			cloudId,
			helpCenterAri,
			queryTerm: queryWord,
			limit: DEFAULT_RESULTS_LIMIT,
		},
	);
	/* eslint-enable @atlassian/relay/unused-fields */

	const articles: ArticleResult[] = useMemo(() => {
		const results = searchResponse?.helpObjectStore_searchArticles?.results;
		if (!results) {
			return [];
		}
		return results.map((result) => ({
			ari: result.ari,
			title: result.title,
			url: result.displayLink,
			type: 'ARTICLE' as const,
			isExternal: result.metadata?.isExternal,
		}));
	}, [searchResponse]);

	const requestTypes: RequestTypeResult[] = useMemo(() => {
		const results = searchResponse?.helpObjectStore_searchRequestTypes?.results;
		if (!results) {
			return [];
		}
		return results.map((result) => ({
			id: result.id,
			title: result.title,
			url: result.displayLink,
			iconUrl: result.iconUrl,
			type: 'REQUEST_TYPE' as const,
		}));
	}, [searchResponse]);

	const hasArticlesError = Boolean(searchResponse?.helpObjectStore_searchArticles?.message);
	const hasRequestTypesError = Boolean(searchResponse?.helpObjectStore_searchRequestTypes?.message);
	const hasBothErrors = hasArticlesError && hasRequestTypesError;

	const allResults = useMemo(() => {
		return [...articles, ...requestTypes];
	}, [articles, requestTypes]);

	const showEmptyState = allResults.length === 0;
	const showResolvedState = allResults.length > 0;

	return (
		<Fragment>
			{showEmptyState &&
				(hasBothErrors ? (
					<HelpObjectSearchError />
				) : (
					<EmptyState
						title={formatMessage(messages.emptyStateTitle)}
						Image={SearchErrorImage}
						content={<FormattedMessage {...messages.emptyStateDescription} />}
					/>
				))}
			{showResolvedState && (
				<SearchDialogContent>
					<Stack xcss={styles.stackContainer}>
						{articles.length > 0 && (
							<>
								<Box xcss={styles.sectionHeading}>
									<Heading as="h3" size="xsmall">
										{formatMessage(messages.resourcesHeading)}
									</Heading>
								</Box>
								<>
									{articles.map((result: ArticleResult) => (
										<Box xcss={styles.searchResultContainer} key={result.ari}>
											<SearchResult
												href={result.url}
												linkComponent={
													result.isExternal ? ExternalLinkComponent : InternalLinkComponent
												}
												title={result.title}
												isCollapsed
												icon={
													<PageIcon
														label=""
														spacing="spacious"
														color={token('color.text.subtle')}
													/>
												}
											/>
										</Box>
									))}
								</>
							</>
						)}
						{requestTypes.length > 0 && (
							<>
								<Box xcss={styles.sectionHeading}>
									<Heading as="h3" size="xsmall">
										{formatMessage(messages.requestFormsHeading)}
									</Heading>
								</Box>
								<>
									{requestTypes.map((result: RequestTypeResult) => (
										<Box xcss={styles.searchResultContainer} key={result.id}>
											<SearchResult
												linkComponent={InternalLinkComponent}
												href={result.url}
												title={result.title}
												isCollapsed
												icon={
													result.iconUrl ? (
														<img css={styles.icon} src={result.iconUrl} alt="" />
													) : (
														<ActivityIcon label="" spacing="spacious" />
													)
												}
											/>
										</Box>
									))}
								</>
							</>
						)}
					</Stack>
				</SearchDialogContent>
			)}
		</Fragment>
	);
};

export const HelpObjectSearchResults = ({
	cloudId,
	helpCenterAri,
	queryWord,
}: HelpObjectSearchResultsProps): React.JSX.Element => {
	if (fg('jsm_help_object_search_split_queries')) {
		return (
			<SplitHelpObjectSearchResults
				key={`${cloudId}:${helpCenterAri}:${queryWord}`}
				cloudId={cloudId}
				helpCenterAri={helpCenterAri}
				queryWord={queryWord}
			/>
		);
	}

	return (
		<CombinedHelpObjectSearchResults
			cloudId={cloudId}
			helpCenterAri={helpCenterAri}
			queryWord={queryWord}
		/>
	);
};

const styles = cssMap({
	externalLink: {
		color: token('color.text.subtle'),
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'none',
		},
		'&:visited': {
			textDecoration: 'none',
		},
	},
	icon: {
		width: '24px',
		height: '24px',
		objectFit: 'contain',
	},
	stackContainer: {
		width: '100%',
		paddingBottom: token('space.200'),
	},
	searchResultContainer: {
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
	},
	sectionHeading: {
		paddingTop: token('space.100'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.250'),
	},
});
